import Appsignal from '@appsignal/javascript';
import Vue from 'vue';
import UAParser from 'ua-parser-js';
import posthog from 'posthog-js';
import errorAction from '../utils/error_action';

let appsignal;

function getComponentContext(vm) {
  if (!vm) return {};

  return {
    component: {
      // eslint-disable-next-line no-underscore-dangle
      name: vm.$options.name || vm.$options._componentTag || 'Unknown',
      // eslint-disable-next-line no-underscore-dangle
      file: vm.$options.__file || null,
      // eslint-disable-next-line no-underscore-dangle
      parent: vm.$parent?.$options?.name || vm.$parent?.$options?._componentTag || null,
    },
    route: {
      name: vm.$route?.name || null,
      path: vm.$route?.path || null,
      from: {
        name: vm.$store?.state?.route?.from?.name || null,
        path: vm.$store?.state?.route?.from?.path || null,
      },
    },
  };
}

function getUserContext(vm) {
  if (!vm || !vm.$auth?.user) return {};

  const user = vm.$auth.user;

  return {
    user: {
      id: user.id,
      role: user.role,
      account_id: user.measureAccountId,
      timezone: user.timezone || null,
      impersonating: user.impersonating || false,
      sso_login: user.ssoLogin || false,
    },
  };
}

function buildFullErrorContext(vm, additionalContext = {}) {
  const componentContext = getComponentContext(vm);
  const userContext = getUserContext(vm);

  const parser = new UAParser();
  const userAgent = parser.getResult();

  const fullContext = {
    url: window.location.href,
    posthog_session_id: posthog.get_session_id() || null,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      browser: {
        name: userAgent.browser.name,
        version: userAgent.browser.version,
      },
      user_agent: navigator.userAgent,
      device: {
        type: userAgent.device.type || 'desktop',
        vendor: userAgent.device.vendor,
        model: userAgent.device.model,
      },
      os: {
        name: userAgent.os.name,
        version: userAgent.os.version,
      },
    },
    ...componentContext,
    ...userContext,
    ...additionalContext,
  };

  return fullContext;
}

function flattenContext(obj, prefix = '') {
  const flattened = {};

  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (value === null || value === undefined) {
      flattened[newKey] = String(value);
    } else if (Array.isArray(value)) {
      flattened[newKey] = JSON.stringify(value);
    } else if (typeof value === 'object' && !(value instanceof Date)) {
      Object.assign(flattened, flattenContext(value, newKey));
    } else {
      flattened[newKey] = String(value);
    }
  });

  return flattened;
}

// Derive a human-readable message from anything that gets reported. Non-Error values (e.g. an
// httpClient rejection like { error, details }, or a bare object) have no `.message`, so
// `new Error(value)` would stringify them to the useless "[object Object]". Serialize instead.
function errorMessage(error) {
  if (error == null) return String(error);
  if (typeof error === 'string') return error;
  if (typeof error.message === 'string' && error.message) return error.message;

  try {
    return JSON.stringify(error);
  } catch (serializationError) {
    return String(error);
  }
}

function enhanceError(error, context = {}, vm = null) {
  if (!error) return error;

  const enhanced = new Error(errorMessage(error));
  enhanced.name = error.name || 'JavaScriptError';
  enhanced.stack = error.stack;
  enhanced.originalError = error;

  try {
    const fullContext = buildFullErrorContext(vm, context);
    enhanced.context = fullContext;
    enhanced.flattenedContext = flattenContext(fullContext);
  } catch (contextError) {
    // Fallback if context building fails
    enhanced.context = {
      url: window.location.href,
      context_error: contextError.message,
      ...context,
    };
    enhanced.flattenedContext = flattenContext(enhanced.context);
  }

  return enhanced;
}

// Transient connectivity failures (offline, flaky network, aborted/timed-out requests) surface as
// axios "Network Error" rejections. They are not application bugs, so an uncaught one bubbling to
// the global handler — or an explicit report — should not be sent to AppSignal as an error.
function isTransientNetworkError(error) {
  if (!error) return false;

  return error.message === 'Network Error'
    || ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(error.code);
}

function isHandledAuthorizationError(error) {
  return error?.error === 'ForbiddenError';
}

function isHandledError(error) {
  return isTransientNetworkError(error) || isHandledAuthorizationError(error);
}

function sendToAppsignal(enhancedError) {
  if (!enhancedError) return;

  const action = errorAction(enhancedError.context);

  appsignal.sendError(enhancedError, (span) => {
    if (action) span.setAction(action);

    span.setTags(enhancedError.flattenedContext || {});
  });
}

if (process.env.RAILS_ENV === 'production' || process.env.RAILS_ENV === 'staging') {
  appsignal = new Appsignal({
    key: process.env.SHAKAPACKER_PUBLIC_APPSIGNAL_FRONTEND_API_KEY,
    revision: process.env.SHAKAPACKER_PUBLIC_SOURCE_VERSION,
  });
  Vue.config.errorHandler = (error, vm, info) => {
    if (isHandledError(error)) return;

    const enhancedError = enhanceError(error, {
      vue_info: info,
      error_source: 'vue_error_handler',
    }, vm);

    if (appsignal) sendToAppsignal(enhancedError);
  };
}

function reportError(error, additionalContext = {}) {
  if (isHandledError(error)) return;

  const enhancedError = enhanceError(error, {
    ...additionalContext,
    error_source: 'manual_report',
  });

  if (appsignal) {
    sendToAppsignal(enhancedError);
  } else {
    // eslint-disable-next-line no-console
    console.log(enhancedError);
  }
}

function reportErrorWithComponent(vm, error, additionalContext = {}) {
  if (isHandledError(error)) return;

  const enhancedError = enhanceError(error, {
    ...additionalContext,
    error_source: 'component_manual',
  }, vm);

  if (appsignal) sendToAppsignal(enhancedError);
}

const errorReporting = {
  install() {
    Vue.reportError = reportError;
    Vue.prototype.$reportError = function performReportError(error, context = {}) {
      reportErrorWithComponent(this, error, context);
    };

    Vue.appsignal = appsignal;
    Vue.prototype.$appsignal = appsignal;
  },
};


Vue.use(errorReporting, {});

export default reportError;
